import React, { useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "../ui/button";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbSeparator,
} from "../ui/breadcrumb";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "../ui/dropdown-menu";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../ui/select";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Label } from "../ui/label";
import {
  ChevronLeftIcon,
  ChevronDownIcon,
  SaveIcon,
  SendIcon,
  DownloadIcon,
  TrashIcon,
  UserPlusIcon,
  SettingsIcon,
  HelpCircleIcon,
  FolderIcon,
} from "lucide-react";

interface DocumentCategory {
  id: string;
  name: string;
}

interface DocumentHeaderProps {
  documentName: string;
  documentPath?: string;
  categories?: DocumentCategory[];
  disableSave?: boolean;
  disableSend?: boolean;
  onBack?: () => void;
  onSave?: (data: {title: string, categoryId: string, notes: string}) => void;
  onSend?: () => void;
  onDelete?: () => void;
  initialTitle?: string;
  initialCategory?: string;
  initialNotes?: string;
}

export const DocumentHeader: React.FC<DocumentHeaderProps> = ({
  documentName = "Untitled Document.pdf",
  documentPath,
  categories = [],
  onBack,
  onSave,
  onSend,
  onDelete,
  disableSave = false,
  disableSend = false,
  initialTitle = "",
  initialCategory = "",
  initialNotes = ""
}) => {
  const { t } = useTranslation(['documents', 'common']);
  const [category, setCategory] = useState<string>(initialCategory);
  const [title, setTitle] = useState<string>(initialTitle || documentName.split(".")[0] || "");
  const [notes, setNotes] = useState<string>(initialNotes);

  // Debug logs
  useEffect(() => {
    console.log("DocumentHeader - Initial props:", { 
      initialTitle, 
      initialCategory, 
      initialNotes 
    });
    console.log("DocumentHeader - Categories:", categories);
    console.log("DocumentHeader - Current state:", { 
      category, 
      title, 
      notes 
    });
  }, [initialTitle, initialCategory, initialNotes, category, title, notes]);

  // No longer auto-select first category; default is "No Category" (empty string)

  // Handle initial category update when it changes from parent
  useEffect(() => {
    if (initialCategory && initialCategory !== category) {
      console.log("Updating category from initialCategory:", initialCategory);
      setCategory(initialCategory);
    }
  }, [initialCategory]);

  // Update title from initialTitle when it changes (e.g., when PDF is uploaded)
  useEffect(() => {
    if (initialTitle && initialTitle !== title) {
      setTitle(initialTitle);
    }
  }, [initialTitle]);

  // Handle save click
  const handleSave = () => {
    if (!title.trim()) {
      alert("Please enter a document title before saving.");
      return;
    }
    
    console.log("DocumentHeader - Sending save request with:", { 
      title, 
      categoryId: category, 
      notes,
      categoryExists: !!category,
      titleExists: !!title,
      categories
    });
    
    if (onSave) {
      onSave({
        title,
        categoryId: category === 'none' ? '' : category,
        notes
      });
    }
  };

  return (
    <header className="border-b border-border bg-card">
      <div className="p-4 border-b border-border">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <Label htmlFor="document-category">{t('documents:editor_category_label')}</Label>
            <Select value={category} onValueChange={setCategory}>
              <SelectTrigger id="document-category">
                <SelectValue placeholder={t('documents:editor_select_category')} />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="none">{t('documents:editor_no_category')}</SelectItem>
                {Array.isArray(categories) && categories.length > 0 && categories.map((category) => (
                  <SelectItem key={category.id} value={category.id.toString()}>
                    {category.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div>
            <Label htmlFor="document-title">{t('documents:editor_title_label')}</Label>
            <Input
              id="document-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder={t('documents:editor_title_placeholder')}
            />
          </div>

          <div>
            <Label htmlFor="document-notes">{t('documents:editor_notes_label')}</Label>
            <Textarea
              id="document-notes"
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder={t('documents:editor_notes_placeholder')}
              className="resize-none h-[38px]"
            />
          </div>
        </div>
      </div>

      <div className="p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={onBack}>
              <ChevronLeftIcon className="h-5 w-5" />
            </Button>

            <Breadcrumb>
              <BreadcrumbList>
                <BreadcrumbItem>
                  <BreadcrumbLink href="/documents">{t('documents:editor_breadcrumb_documents')}</BreadcrumbLink>
                </BreadcrumbItem>
                <BreadcrumbSeparator />
                <BreadcrumbItem>
                  <BreadcrumbLink href="#">{documentName}</BreadcrumbLink>
                </BreadcrumbItem>
              </BreadcrumbList>
            </Breadcrumb>            
          </div>

          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleSave} disabled={disableSave}>
              <SaveIcon className="h-4 w-4 mr-2" />
              {t('common:actions.save')}
            </Button>

            <Button size="sm" onClick={onSend} disabled={disableSend}>
              <SendIcon className="h-4 w-4 mr-2" />
              {t('common:actions.send')}
            </Button>
           
          </div>
        </div>
      </div>
    </header>
  );
};
